/**
 * Sync.Land REST API wrapper.
 *
 * Reads its base URL from VITE_SYNCLAND_API (set in .env.local for dev), falls
 * back to production. All calls attach the streamer's Personal Access Token
 * as a Bearer credential.
 *
 * When the backend endpoints don't yet exist (v0.1 scaffold), the store
 * uses the `stubMode` flag to return canned responses so the UI is testable
 * end-to-end without a live backend. Flip to false once /streamer/* ships.
 */

// Production ALWAYS talks to sync.land. The override is honoured only under
// the dev server, because Vite ranks .env.local above .env.production — so a
// developer's local file silently shipped `http://sync.local/...` into the
// deployed bundle, and the dock could never reach prod. Gating on
// import.meta.env.DEV makes that class of mistake impossible rather than
// relying on remembering to move a file before building.
const PROD_API = 'https://www.sync.land/wp-json/FML/v1';

// Prefer the origin the dock was actually served from. sync.land and
// www.sync.land BOTH return 200 for /dock/ with no canonical redirect, and the
// PAT is stored per origin, so a hardcoded host meant a streamer could set up on
// one and come back on the other to an empty dock and an apparently dead token,
// with nothing on screen to explain why. Same-origin also drops a needless CORS
// round trip.
function resolveApiBase() {
  if (import.meta.env && import.meta.env.DEV && import.meta.env.VITE_SYNCLAND_API) {
    return import.meta.env.VITE_SYNCLAND_API;
  }
  try {
    if (typeof location !== 'undefined' && /(^|[.])sync[.]land$/.test(location.hostname)) {
      return location.origin + '/wp-json/FML/v1';
    }
  } catch (e) { /* fall through to the pinned host */ }
  return PROD_API;
}
const API_BASE = resolveApiBase();

// Stubs default off — the backend is live. Set `?stub=1` in the URL, or
// VITE_STUB_MODE=1 in .env.local, to force stubs during dev without a token.
// Dev server only: a stub clearance says yes to any song id without asking the
// site, so a production build must not be able to switch it on. Stub
// clearances also carry no stream_url, so even in dev they play no audio.
const DEV_BUILD = !!(import.meta.env && import.meta.env.DEV);
const urlStub = DEV_BUILD && typeof location !== 'undefined' && new URLSearchParams(location.search).get('stub') === '1';
const envStub = DEV_BUILD && !!(import.meta.env && import.meta.env.VITE_STUB_MODE === '1');
let stubMode  = urlStub || envStub;

export function setStubMode(on) { stubMode = DEV_BUILD && !!on; }
export function isStub() { return stubMode; }

// -------------------------------------------------------------------------
// PAT storage (localStorage keyed to the dock so multiple docks on one
// machine can hold different tokens — future feature)
// -------------------------------------------------------------------------
const TOKEN_KEY   = 'syncland_obs_player_pat';
const PLAYLIST_KEY = 'syncland_obs_player_last_playlist';

export function saveToken(pat) { localStorage.setItem(TOKEN_KEY, pat); }
export function loadToken()   { return localStorage.getItem(TOKEN_KEY) || ''; }
export function clearToken()   { localStorage.removeItem(TOKEN_KEY); }
export function saveLastPlaylist(id) { localStorage.setItem(PLAYLIST_KEY, String(id)); }
export function clearLastPlaylist()   { localStorage.removeItem(PLAYLIST_KEY); }
export function loadLastPlaylist()    { return localStorage.getItem(PLAYLIST_KEY) || ''; }

// -------------------------------------------------------------------------
// Demo mode
//
// Without a token the dock used to throw 401 and show a sign-in wall, so a
// stranger who pasted the URL into OBS heard nothing at all. Demo mode serves a
// small curated, consent-gated playlist from public endpoints instead, so step
// one becomes "paste a URL and hear music" and signing up is the upgrade.
//
// Demo plays are deliberately not logged: the usage ledger is keyed to a user
// and records a licence-relevant play, and anonymous demo playback is neither.
// -------------------------------------------------------------------------
export function isDemo() { return !stubMode && !loadToken(); }

async function publicRequest(path) {
  const r = await fetch(API_BASE + path, { headers: { 'Accept': 'application/json' } });
  if (!r.ok) {
    const err = new Error('API ' + r.status + ': ' + (await r.text()).slice(0, 300));
    err.status = r.status;
    throw err;
  }
  return r.json();
}

// -------------------------------------------------------------------------
// Low-level fetch wrapper
// -------------------------------------------------------------------------
async function request(path, opts = {}) {
  const token = loadToken();
  if (!token) { const err = new Error('No PAT - user needs to sign in first'); err.status = 401; err.isAuth = true; throw err; }
  const url = `${API_BASE}${path}`;
  const r = await fetch(url, {
    ...opts,
    headers: {
      'Accept': 'application/json',
      ...(opts.body ? { 'Content-Type': 'application/json' } : {}),
      'Authorization': `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) {
    const text = await r.text();
    // Carry the status. Without it a caller can do nothing but print the raw
    // body, which is how a visitor with a dead token ended up reading
    // {"code":"rest_forbidden",...} instead of being asked to sign in.
    const err = new Error(`API ${r.status}: ${text.slice(0, 300)}`);
    err.status = r.status;
    err.isAuth = (r.status === 401 || r.status === 403);
    throw err;
  }
  return r.json();
}

// -------------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------------

export async function whoAmI() {
  if (stubMode) return { user_id: 8617, display_name: 'Creepzz (stub)', artist_ids: [11732] };
  if (isDemo()) return { user_id: 0, display_name: 'Demo', artist_ids: [], demo: true };
  return request('/streamer/me');
}

export async function listPlaylists() {
  if (stubMode) return stubPlaylists();
  if (isDemo()) return publicRequest('/streamer/demo/playlists');
  return request('/streamer/playlists');
}

/**
 * The most recent clearance check, kept so the UI can *show* that a real
 * public-API call verified the licence. M4 acceptance criterion 3 is
 * "an external application is able to verify a sync license via public API",
 * and the evidence asked for is a link to the successful request plus a
 * screen recording — so the request itself has to be visible on camera,
 * not just its result.
 */
let lastVerification = null;
export function getLastVerification() { return lastVerification; }

export async function getTrackClearance(song_id) {
  const path = `/streamer/track/${song_id}/clearance`;
  const url  = `${API_BASE}${path}`;
  const t0   = (typeof performance !== 'undefined' ? performance.now() : Date.now());

  if (stubMode) {
    const data = await stubClearance(song_id);
    lastVerification = { method: 'GET', url, status: 200, ms: 0,
                         at: new Date().toISOString(), stub: true, response: data };
    return data;
  }

  if (isDemo()) {
    const demoPath = '/streamer/demo/track/' + song_id + '/clearance';
    const data = await publicRequest(demoPath);
    if (!data || typeof data !== 'object' || typeof data.can_stream !== 'boolean') {
      throw new Error('The licence check returned something unreadable');
    }
    lastVerification = { method: 'GET', url: API_BASE + demoPath, status: 200,
                         ms: Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0),
                         at: new Date().toISOString(), demo: true, response: data };
    return data;
  }

  const token = loadToken();
  if (!token) { const err = new Error('No PAT - user needs to sign in first'); err.status = 401; err.isAuth = true; throw err; }
  const r = await fetch(url, {
    headers: { 'Accept': 'application/json', 'Authorization': `Bearer ${token}` },
  });
  const ms   = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
  const text = await r.text();
  let data = null;
  try { data = JSON.parse(text); } catch (e) { /* non-JSON body */ }

  lastVerification = { method: 'GET', url, status: r.status, ms,
                       at: new Date().toISOString(), stub: false,
                       response: data !== null ? data : text.slice(0, 400) };

  if (!r.ok) {
    const err = new Error(`API ${r.status}: ${text.slice(0, 300)}`);
    err.status = r.status;
    err.isAuth = (r.status === 401 || r.status === 403);
    throw err;
  }
  // A 200 that is not a clearance (a proxy page, a PHP notice ahead of the
  // JSON) is a failed check, not a verdict. The player fails closed on it.
  if (!data || typeof data !== 'object' || typeof data.can_stream !== 'boolean') {
    const err = new Error('The licence check returned something unreadable');
    err.status = r.status;
    throw err;
  }
  return data;
}

/**
 * Close a usage out: how much of the track was actually heard, and why it
 * stopped. Fire-and-forget, and uses sendBeacon when the page is going away
 * so a closing dock still reports the ending it already knows.
 */
export async function completePlay(usage_id, { seconds = 0, duration = 0, reason = 'stopped' } = {}) {
  if (!usage_id) return;
  if (stubMode) { console.info('[stub] completePlay', { usage_id, seconds, duration, reason }); return; }
  const url  = `${API_BASE}/streamer/usage/${usage_id}/complete`;
  const body = JSON.stringify({ seconds: Math.round(seconds), duration: Math.round(duration), reason });
  const token = loadToken();
  if (!token) return;
  try {
    await fetch(url, {
      method: 'POST',
      keepalive: true,          // survives the tab or dock closing mid-flight
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body,
    });
  } catch (e) { /* never let bookkeeping break playback */ }
}

/**
 * Record a play. playlist_id says which playlist it was played from: the usage
 * ledger stores it, and the site uses it to pick the playlist a first-play
 * licence is issued under.
 */
export async function logPlay(song_id, seconds, playlist_id = 0) {
  if (isDemo()) return { ok: true, demo: true };   // see the note above: not ledgered
  if (stubMode) {
    console.info('[stub] logPlay', { song_id, seconds, playlist_id });
    return { ok: true };
  }
  const body = { seconds };
  if (playlist_id) body.playlist_id = Number(playlist_id);
  return request(`/streamer/track/${song_id}/played`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

// -------------------------------------------------------------------------
// Stubs — keep the UI runnable while backend catches up
// -------------------------------------------------------------------------

function stubPlaylists() {
  return {
    ok: true,
    playlists: [
      { id: 1, name: 'Chill / stream focus',   track_count: 12, cover_url: '' },
      { id: 2, name: 'High-energy runs',       track_count: 18, cover_url: '' },
      { id: 3, name: 'Cardano ecosystem picks', track_count: 8,  cover_url: '' },
    ],
    tracks: {
      1: [
        { song_id: 11907, title: 'Island Vibe',      artist: 'Creepzz feat. Kosi Sia' },
        { song_id: 11903, title: 'Take Me Back',    artist: 'Creepzz x Jordan Grace feat. Kosi Sia' },
        { song_id: 11566, title: 'Turn Tail and Run', artist: 'Cullah' },
      ],
    },
  };
}

function stubClearance(song_id) {
  const catalog = {
    11907: { title: 'Island Vibe',  artist: 'Creepzz', slug: 'island-vibe' },
    11903: { title: 'Take Me Back', artist: 'Creepzz', slug: 'take-me-back' },
    11566: { title: 'Turn Tail and Run', artist: 'Cullah', slug: 'turn-tail-and-run' },
  };
  const meta = catalog[song_id] || { title: `Track #${song_id}`, artist: 'Unknown', slug: `song-${song_id}` };
  return {
    ok: true,
    can_stream: true,
    tier: 'SLFS-v1',
    tier_label: 'Free Sync License',
    attribution_required: true,
    attribution_text: `Music: ${meta.artist} via Sync.Land · sync.land/song/${meta.slug}`,
    stream_url: '', // no real audio in stub - player renders "stub mode" state
    reason_if_blocked: null,
    song: { id: song_id, title: meta.title, artist: meta.artist, slug: meta.slug },
  };
}
